import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import CustomerTabsJobFile from "../../components/CustomerTabsJobFile";
import JobFileInvoiceTable from "../../components/Table/JobFileInvoiceTable";
import InvoiceEditModal from "../../components/Modal/InvoiceEditModal";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import { openReceiptReportByPayRow } from "../../../utils/ReportUtils";

export default function JobFileInvoiceRecordPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const job = location.state?.job || null;
  const backLink = location.state?.backLink || "/invoice-record";
  const backLabel = location.state?.backLabel || "บันทึกการชำระเงิน";

  useEffect(() => {
    fetchInvoiceData();
  }, [id]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      
      // ดึงข้อมูลสัญญา
      const contractRes = await api.get(`/get-contract/${id}`);
      if (contractRes.data.status !== "OK") {
        throw new Error(contractRes.data.message || "ไม่สามารถดึงข้อมูลสัญญาได้");
      }

      const header = contractRes.data.header || {};
      setData({
        Contract_No: header.Doc_No || id,
        CustomerName: header.DisplayName || `${header.AR_Name || ""} ${header.AR_LastName || ""}`.trim() || "-",
      });

      // ดึงข้อมูล invoice จาก CT_Pay
      const invoiceRes = await api.get(`/get-contract-invoices/${id}`);
      if (invoiceRes.data.status === "OK") {
        const invoiceList = (invoiceRes.data.result || []).map((inv) => {
          // กำหนดสถานะ
          let status = "pending";
          if (inv.Invoice_Status === 1) {
            status = "paid";
          } else if (inv.Invoice_Status === 2) {
            status = "overdue";
          } else {
            // ตรวจสอบว่าเกินกำหนดหรือไม่ (ถ้า CT_Due_Date ผ่านมาแล้วและยังไม่ชำระ)
            if (inv.CT_Due_Date) {
              const payDate = new Date(inv.CT_Due_Date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (payDate < today && inv.Invoice_Status === 0) {
                status = "overdue";
              }
            }
          }

          return {
            CT_Pay_Id: inv.CT_Pay_Id,
            Doc_Id: inv.Doc_Id,
            Invoice_No: inv.Invoice_No || "-",
            Line: inv.Line,
            Date: inv.CT_Due_Date 
              ? new Date(inv.CT_Due_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Total: inv.CT_Due_Amount || 0,
            ToPay: inv.CT_Due_Amount || 0,
            Paid: inv.Paid_Amount || 0,
            PayDate: inv.Paid_Date
              ? new Date(inv.Paid_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Remark: inv.Remark || "-",
            Ref1: inv.Ref1 || "-",
            Ref2: inv.Ref2 || "-",
            Payment_Method:
              inv.Payment_Method != null && inv.Payment_Method !== ""
                ? String(inv.Payment_Method)
                : "1",
            Withholding_Tax:
              inv.Withholding_Tax === 1 || inv.Withholding_Tax === true,
            Bank:
              inv.Bank && String(inv.Bank).trim() !== "" ? inv.Bank : "-",
            Check_No:
              inv.Check_No && inv.Check_No !== "" ? inv.Check_No : "-",
            Check_Date: inv.Check_Date
              ? new Date(inv.Check_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Transfer_Bank:
              inv.Transfer_Bank && String(inv.Transfer_Bank).trim() !== ""
                ? inv.Transfer_Bank
                : "-",
            Transfer_Account:
              inv.Transfer_Account && inv.Transfer_Account !== ""
                ? inv.Transfer_Account
                : "-",
            Card_Bank:
              inv.Card_Bank && String(inv.Card_Bank).trim() !== ""
                ? inv.Card_Bank
                : "-",
            Card_No: inv.Card_No && inv.Card_No !== "" ? inv.Card_No : "-",
            Status: status,
            Invoice_Status: inv.Invoice_Status || 0,
            Is_VAT: inv.Is_VAT === 1 || inv.Is_VAT === true,
          };
        });

        setInvoices(invoiceList);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.message || "ไม่สามารถดึงข้อมูลใบแจ้งหนี้ได้",
      });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleModalClose = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  const handleModalSuccess = () => {
    fetchInvoiceData(); // รีเฟรชข้อมูลหลังจากบันทึกสำเร็จ
  };

  const handlePrintInvoice = (row) => {
    openReceiptReportByPayRow(row);
  };

  if (loading || !data) {
    return (
      <div className="flex">
        <Sidebar />
        <div
          className="flex-1 min-h-screen bg-gray-50"
          style={{ paddingLeft: "var(--sidebar-width)" }}
        >
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]"></div>
              <p className="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      {/* SIDEBAR */}
      <Sidebar />

      {/* MAIN */}
      <main
        className="flex-1 h-screen overflow-auto bg-gray-50 transition-all duration-300"
        style={{ marginLeft: "var(--sidebar-width)" }}
      >
        {/* TOP BAR */}
        <Topbar
          breadcrumbs={[
            { label: "งานบัญชี", link: "/invoice-record" },
            { label: backLabel, link: backLink },
            { label: data.Contract_No },
          ]}
        />

        {/* CONTENT */}
        <div className="px-8 py-6">
          {/* HEADER */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">
                บันทึกข้อมูลการชำระเงิน - สัญญา {data.Contract_No}
              </h1>
              <p className="text-sm text-gray-600">
                ลูกค้า: {data.CustomerName}
              </p>
            </div>
            <button
              onClick={() => navigate(`/jobfile/${id}/invoice`)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Icon icon="mdi:arrow-left" className="w-5 h-5" />
              กลับไปหน้าใบแจ้งหนี้
            </button>
          </div>

          {/* INFO CARD */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon icon="mdi:information" className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-blue-900 mb-1">
                  คำแนะนำการใช้งาน
                </h3>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>เลือกใบแจ้งหนี้ที่ต้องการบันทึกข้อมูลการชำระเงิน</li>
                  <li>คลิกปุ่ม "แก้ไข" เพื่อเปิดฟอร์มบันทึกข้อมูล</li>
                  <li>กรอกข้อมูลการชำระเงินที่ลูกค้านำมา (ยอดชำระ, วันที่ชำระ, หมายเหตุ)</li>
                  <li>แนบไฟล์สลิปการโอนเงินหรือเอกสารที่เกี่ยวข้อง (ถ้ามี)</li>
                  <li>คลิก "บันทึก" เพื่อบันทึกข้อมูล</li>
                </ul>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="bg-white rounded-lg shadow-sm">
            <JobFileInvoiceTable
              invoices={invoices}
              onEdit={handleEditInvoice}
              onPrint={handlePrintInvoice}
              onDelete={(row) => {
                Swal.fire({
                  title: "ยืนยันการลบ",
                  text: `คุณต้องการลบใบแจ้งหนี้ ${row.Invoice_No} ใช่หรือไม่?`,
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#d33",
                  cancelButtonColor: "#3085d6",
                  confirmButtonText: "ลบ",
                  cancelButtonText: "ยกเลิก",
                }).then((result) => {
                  if (result.isConfirmed) {
                    // TODO: Implement delete API call
                    Swal.fire("ลบแล้ว", "ลบใบแจ้งหนี้สำเร็จ", "success");
                    fetchInvoiceData();
                  }
                });
              }}
            />
          </div>
        </div>
      </main>

      {/* INVOICE EDIT MODAL */}
      {showInvoiceModal && selectedInvoice && (
        <InvoiceEditModal
          isOpen={showInvoiceModal}
          onClose={handleModalClose}
          invoice={selectedInvoice}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
}

